import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { ThunkGetTenantCountry } from '@store/slices/tenant/thunks';

import phoneWithCharactersValidator from '@utils/validators/phoneWithCharactersValidator';
import { requiredValidator } from '@utils/validators';

import ReusableButton from '@common/Button/Button';
import PhoneField from '@common/PhoneField';
import Loading from '@common/Loading/Loading';

import useSharedStyles from '@components/CrmTicket/sharedStyles';
import { usePhoneInitialData } from '../../../../../../../hooks/usePhoneInitialData';
import { useCountryCodesOptions } from '../../../../../../../hooks/useCountryCodesOptions';

const validate = values => ({
  phone: phoneWithCharactersValidator(values.phone, 9, true),
  countryCode: requiredValidator(values.countryCode),
});

const PhoneForm = ({
  onSubmit,
  initialValues,
  countryCodesOptions,
  initialIcon,
  onClose,
}) => {
  const sharedStyles = useSharedStyles();

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <PhoneField
            isRequired
            phoneName="phone"
            phoneCodeName="countryCode"
            label="Phone Number"
            countryCodesOptions={countryCodesOptions}
            initialIcon={initialIcon}
          />

          <div className={sharedStyles.dialogActionsContainer}>
            <ReusableButton label="Cancel" onClick={onClose} />
            <ReusableButton label="Submit" type="submit" />
          </div>
        </form>
      )}
    />
  );
};

const AddPhoneModal = ({ open, onClose, onAddPhone }) => {
  const [loading, setLoading] = useState(false);
  const { countryCodesOptions, countries } = useCountryCodesOptions();
  const [defaultTenantCodes, setDefaultTenantCodes] = useState({
    tenantCountryCode: '',
    tenantDialCode: '',
  });
  const [tenantCountry, setTenantCountry] = useState(null);

  const dispatch = useDispatch();

  const submit = useCallback(
    values => {
      onAddPhone(values);
    },
    [onAddPhone],
  );

  const onMount = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatch(ThunkGetTenantCountry()).unwrap();
      setTenantCountry(res.data);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const {
    initialIcon,
    countryCodeFormValue,
    nationalNumber,
  } = usePhoneInitialData({
    phone: '',
    countries,
    defaultTenantCodes,
  });

  useEffect(() => {
    if (tenantCountry && countries.length) {
      const country = countries?.find(el => el.code === tenantCountry);
      setDefaultTenantCodes({
        tenantCountryCode: country?.code ?? '',
        tenantDialCode: country?.dialCode ?? '',
      });
    }
  }, [countries, tenantCountry]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add User</DialogTitle>
      <DialogContent>
        {loading ? (
          <Loading />
        ) : (
          <PhoneForm
            onSubmit={submit}
            initialValues={{
              phone: nationalNumber,
              countryCode: countryCodeFormValue,
            }}
            countryCodesOptions={countryCodesOptions}
            initialIcon={initialIcon}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

AddPhoneModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddPhone: PropTypes.func.isRequired,
};
PhoneForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  countryCodesOptions: PropTypes.array.isRequired,
  initialIcon: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddPhoneModal;
